import { MsalService } from '@azure/msal-angular';
import { catchError, from, Observable, switchMap, tap, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { AccountInfo } from '@azure/msal-browser';

@Injectable()
export class AuthInterceptor {
  private readonly msalService = inject(MsalService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const account = this.msalService.instance.getActiveAccount();
    
    if (!account) {
      return next.handle(request);
    }

    if (!this.checkToken(account)) {
      return throwError(() => new Error('Token expired'));
    }

    // Use ID token scopes for refreshing the ID token
    const idTokenScopes = ['openid', 'profile', 'email'];

    return from(this.msalService.instance.acquireTokenSilent({ scopes: idTokenScopes, account }))
      .pipe(
        switchMap(result => {
          const idToken = result.idToken;

          const headers: Record<string, string> = {
            Authorization: `Bearer ${idToken}`
          };

          const cloned = request.clone({ setHeaders: headers });

          return next.handle(cloned).pipe(
            tap(),
            catchError((err: unknown) => {
              return throwError(() => err);
            })
          );
        }),
        catchError((error: unknown) => {
          return throwError(() => error);
        })
      );
  }

  checkToken(account: AccountInfo) {
    const idTokenClaims = account.idTokenClaims as any;
    const exp = idTokenClaims?.exp;
    const isExpired = exp && Date.now() >= exp * 1000;

    if (isExpired) {
      console.warn('Token is expired, redirecting if needed');

      if (!this.isRedirecting()) {
        this.setRedirecting(true);
        this.msalService.loginRedirect();
      }
      
      return false;
    } else {
      return true;
    }
  }

  private isRedirecting(): boolean {
    return sessionStorage.getItem('redirecting') === 'true';
  }

  private setRedirecting(flag: boolean): void {
    sessionStorage.setItem('redirecting', flag ? 'true' : 'false');
  }
}